import { toast } from "react-toastify";
import { Box, styled } from "@mui/material";
import { Alert, Checkbox, Col, DatePicker, Form, Modal, Radio, Row, Switch } from "antd";
import { FormInstance } from "antd/lib";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import { generateCode } from "../../utils/utils";
import { useMe } from "../../auth/MeContext";
import { PERM } from "../../auth/permissions";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import { FIELD_HEIGHT } from "../common/fields/fieldStyles";
import SelectField from "../common/fields/SelectField";
import "./addModal.scss";

interface SelectOption {
  value: number;
  label: string;
}

interface AddModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  reload?: () => void;
  isEdit?: boolean;
  couponData?: any;
}

const AddModal: React.FC<AddModalProps> = ({ open, setOpen, reload, isEdit = false, couponData }) => {
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };
  const formData = new FormData();
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation();
  const { can } = useMe();
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [salonOptions, setSalonOptions] = useState<SelectOption[]>([]);
  const [userList, setUserList] = useState([]);
  const [selectedDiscountType, setSelectedDiscountType] = useState("");
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const today = dayjs().startOf("day");

  const getCountries = async () => {
    await axiosInstance
      .get(pathAPI.couponLookupCountries)
      .then((response: any) => {
        setCountryOptions(
          (response.data?.data ?? []).map((item: any) => ({ value: item.id, label: item.name }))
        );
      })
      .catch(() => setCountryOptions([]));
  };

  // Salons of the chosen countries (dropdown country -> salon list)
  const loadSalons = async (countryIds: number[], keep: SelectOption[] = []) => {
    if (!countryIds || countryIds.length === 0) {
      setSalonOptions(keep);
      return;
    }
    await axiosInstance
      .get(pathAPI.couponLookupOutlets, { params: { countryIds: countryIds.join(",") } })
      .then((response: any) => {
        const loaded: SelectOption[] = (response.data?.data ?? []).map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        const extra = keep.filter((k) => !loaded.some((l) => l.value === k.value));
        setSalonOptions([...loaded, ...extra]);
      })
      .catch(() => setSalonOptions(keep));
  };

  const getUsers = async () => {
    await axiosInstance
      .get(pathAPI.appUsers)
      .then((res: any) => {
        const endData = res.data.data.map((item: any) => ({
          value: item.id,
          label: item.full_name || item.email
        }));
        setUserList(endData);
      })
      .catch((err: any) => {
        console.log("====== reload data table error: ", err);
        setUserList([]);
      });
  }

  const buildCouponPayload = () => {
    const form = formRef.current;
    if (!form) return {};
    
    return {
      active: form.getFieldValue("active") ?? false,
      coupon_name: form.getFieldValue("couponName"),
      coupon_code: form.getFieldValue("couponCode"),
      coupon_type: form.getFieldValue("couponType"),
      coupon_amount: Number(form.getFieldValue("discountAmount")),
      max_discount_per_customer: Number(form.getFieldValue("numberCouponPerUser")),
      number_of_coupon: Number(form.getFieldValue("totalCoupon")),
      start_time: new Date(form.getFieldValue("startTime")).toISOString(),
      end_time: new Date(form.getFieldValue("endTime")).toISOString(),
      description: form.getFieldValue("description"),
      apply_for_first_order_only: form.getFieldValue("applyToFirstOrderOnly"),
      app_user_ids: form.getFieldValue("appUserIds") || [],
      target_type: form.getFieldValue("targetType") || "COUNTRY",
      country_ids:
        form.getFieldValue("targetType") === "OUTLET" ? [] : form.getFieldValue("countryIds") || [],
      salon_ids:
        form.getFieldValue("targetType") === "OUTLET" ? form.getFieldValue("salonIds") || [] : [],
      min_spend: form.getFieldValue("miniumSpend") ? Number(form.getFieldValue("miniumSpend")) : undefined,
      max_spend: form.getFieldValue("maxiumSpend") ? Number(form.getFieldValue("maxiumSpend")) : undefined,
    };
  };

  const onFinish = async () => {
    try {
      await form.validateFields();
      setLoadingBtn(true);
      
      const payload = buildCouponPayload();
      console.log(isEdit ? 'Update coupon payload:' : 'Create coupon payload:', payload);
      
      if (isEdit) {
        updateCoupon(payload);
      } else {
        createCoupon(payload);
      }
    } catch (error) {
      console.log('Form validation failed:', error);
    }
  }

  const createCoupon = async (formData: any) => {
    try {
      const res = await axiosInstance.post(pathAPI.coupon, formData);
      toast.success(t("coupon.form.created_ok"));
      setOpen(false);
      formRef.current?.resetFields();
      setIsFormValid(false);
      if (reload) {
        reload();
      }
    } catch (err) {
      toast.error(t("coupon.form.created_err"));
    } finally {
      setLoadingBtn(false);
    }
  }

  const updateCoupon = async (formData: any) => {
    try {
      await axiosInstance.put(`${pathAPI.coupon}/${couponData?.id}`, formData);
      toast.success(t("coupon.form.updated_ok"));
      setOpen(false);
      formRef.current?.resetFields();
      setIsFormValid(false);
      if (reload) {
        reload();
      }
    } catch (err) {
      console.log('Update error:', err);
      toast.error(t("coupon.form.updated_err"));
    } finally {
      setLoadingBtn(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    formRef.current?.resetFields();
    setIsFormValid(false);
    if (isEdit && couponData) {
      setSelectedDiscountType("");
    }
  };

  const handleFormValuesChange = () => {
    setTimeout(() => {
      form.validateFields({ validateOnly: true })
        .then(() => {
          setIsFormValid(true);
        })
        .catch(() => {
          setIsFormValid(false);
        });
    }, 100);
  };

  const populateFormData = () => {
    if (couponData && formRef.current) {
      const startTime = dayjs(new Date(couponData.start_time).toISOString());
      const endTime = dayjs(new Date(couponData.end_time).toISOString());

      setTimeout(() => {
        form.setFieldsValue({
          couponName: couponData.coupon_name || couponData.name,
          couponCode: couponData.coupon_code || couponData.code,
          couponType: couponData.coupon_type || couponData.type,
          discountAmount: couponData.coupon_amount || couponData.amount,
          numberCouponPerUser: couponData.max_discount_per_customer || couponData.max_per_customer,
          totalCoupon: couponData.number_of_coupon || couponData.total_coupons,
          startTime: startTime,
          endTime: endTime,
          description: couponData.description,
          applyToFirstOrderOnly: !!couponData.apply_for_first_order_only,
          active: couponData.active === true || couponData.active === 1,
          miniumSpend: couponData.min_spend,
          maxiumSpend: couponData.max_spend,
          appUserIds: couponData.app_users ? couponData.app_users.map((item: any) => item.id) : [],
          salonIds: couponData.salons ? couponData.salons.map((item: any) => item.id) : [],
          targetType: couponData.target_type || "OUTLET",
          countryIds: couponData.countries ? couponData.countries.map((item: any) => item.id) : [],
        });
        if ((couponData.target_type || "OUTLET") === "OUTLET") {
          const current: SelectOption[] = (couponData.salons ?? []).map((item: any) => ({
            value: item.id,
            label: item.business_name || item.salon_name || item.name || String(item.id),
          }));
          loadSalons((couponData.countries ?? []).map((item: any) => item.id), current);
        }
        
        setSelectedDiscountType(couponData.coupon_type || couponData.type);
        setIsFormValid(true);
      }, 100);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && couponData) {
        populateFormData();
      } else {
        const randomCode = generateCode(12);
        setSalonOptions([]);
        setTimeout(() => {
          formRef.current?.setFieldValue("couponCode", randomCode);
          form.setFieldValue("couponCode", randomCode);
          form.setFieldValue("targetType", "COUNTRY");
          
          setTimeout(() => {
            form.validateFields({ validateOnly: true })
              .then(() => setIsFormValid(true))
              .catch(() => setIsFormValid(false));
          }, 200);
        }, 100);
      }
      setIsFormValid(false);
    } else {
      form.resetFields();
      setSelectedDiscountType("");
    }
  }, [open, form, isEdit, couponData]);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (open) {
      getCountries();
    }
  }, [open]);

  useEffect(() => {
    if (isEdit && couponData && userList.length > 0) {
      populateFormData();
    }
  }, [isEdit, couponData, userList]);

  const targetType = Form.useWatch("targetType", form);
  const needsApproval = !can(PERM.COUPON_APPROVE);

  return (
    <Modal
      title=""
      centered
      open={open}
      onOk={() => setOpen(false)}
      onCancel={handleCancel}
      width={1200}
      okText={t("coupon.form.save")}
      cancelText={t("btn.cancel")}
      className="add_modal"
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={handleCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton
            onClick={onFinish}
            disabled={!isFormValid || loadingBtn}
            loading={loadingBtn}
          >
            {t("btn.ok")}
          </ActionButton>
        </Box>
      }
    >
      <div className="header_style">{isEdit ? t("coupon.form.edit_title") : t("coupon.form.add_title")}</div>
      {needsApproval && (
        <Alert
          type="info"
          showIcon
          style={{ margin: "8px 0" }}
          message={
            isEdit && couponData?.approval_status === "APPROVED"
              ? t("coupon.form.notice_resubmit")
              : t("coupon.form.notice_approval")
          }
        />
      )}
      {isEdit && couponData?.approval_status === "REJECTED" && couponData?.reject_reason && (
        <Alert
          type="error"
          showIcon
          style={{ margin: "8px 0" }}
          message={t("coupon.form.rejected", { reason: couponData.reject_reason })}
        />
      )}
      <StyledForm 
        {...layout} 
        ref={formRef} 
        onFinish={onFinish} 
        form={form}
        onValuesChange={handleFormValuesChange}
      >
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponName"}
                label={t("coupon.form.name")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("coupon.form.name_required") }]}
              >
                <InputField onChange={() => handleFormValuesChange()} />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponCode"}
                label={t("coupon.form.code")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("coupon.form.code_required") }]}
              >
                <InputField onChange={() => handleFormValuesChange()} />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponType"}
                label={t("coupon.form.discount_type")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("coupon.form.discount_type_required") }]}
              >
                <SelectField
                  placeholder={t("coupon.form.select_type")}
                  searchable={false}
                  matchAnchorWidth
                  onChange={(value) => {
                    setSelectedDiscountType(String(value ?? ""));
                    handleFormValuesChange();
                  }}
                  options={[
                    {
                      value: "fixPrice",
                      label: t("coupon.form.fix_price"),
                    },
                    {
                      value: "percent",
                      label: t("coupon.form.percent"),
                    },
                  ]}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"discountAmount"}
                label={`${t("coupon.form.discount_value")} ${selectedDiscountType === "fixPrice" ? "($)" : selectedDiscountType === "percent" ? "(%)" : ""}`}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: t("coupon.form.discount_amount_required") },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue <= 0) {
                        return Promise.reject(new Error(t("coupon.form.discount_gt_zero")));
                      }
                      if (selectedDiscountType === "percent" && numValue > 100) {
                        return Promise.reject(new Error(t("coupon.form.percent_max")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"miniumSpend"}
                label={t("coupon.form.min_spend")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 0) {
                        return Promise.reject(new Error(t("coupon.form.min_spend_err")));
                      }
                      return Promise.resolve();
                    },
                  }
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"maxiumSpend"}
                label={t("coupon.form.max_spend")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 0) {
                        return Promise.reject(new Error(t("coupon.form.max_spend_err")));
                      }
                      return Promise.resolve();
                    },
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const minSpend = getFieldValue('miniumSpend');
                      if (minSpend && Number(value) <= Number(minSpend)) {
                        return Promise.reject(new Error(t("coupon.form.max_gt_min")));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"startTime"}
                label={t("coupon.form.start_time")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("coupon.form.start_time_required") }]}
              >
                <DatePicker
                  format="YYYY/MM/DD HH:mm"
                  placeholder={t("coupon.form.start_time")}
                  style={{ width: "100%", height: FIELD_HEIGHT, borderRadius: 6 }}
                  showTime={true}
                  disabledDate={(current: Dayjs) => {
                    return current && current < today;
                  }}
                  onChange={() => {
                    form.validateFields(['endTime']);
                    handleFormValuesChange();
                  }}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"endTime"}
                label={t("coupon.form.end_time")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: t("coupon.form.end_time_required") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const startTime = getFieldValue('startTime');
                      if (startTime && value && value.isBefore(startTime)) {
                        return Promise.reject(new Error(t("coupon.form.end_after_start")));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="YYYY/MM/DD HH:mm"
                  placeholder={t("coupon.form.end_time")}
                  style={{ width: "100%", height: FIELD_HEIGHT, borderRadius: 6 }}
                  showTime={true}
                  // Disable all days before startTime
                  disabledDate={(current: Dayjs) => {
                    const startTime = form.getFieldValue('startTime');
                    if (!startTime) return false;
                    return current && current < dayjs(startTime).startOf('day');
                  }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"totalCoupon"}
                label={t("coupon.form.total")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: t("coupon.form.total_required") },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 1 || !Number.isInteger(numValue)) {
                        return Promise.reject(new Error(t("coupon.form.positive_int")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ min: "1" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"numberCouponPerUser"}
                label={t("coupon.form.per_user")}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: t("coupon.form.per_user_required") },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 1 || !Number.isInteger(numValue)) {
                        return Promise.reject(new Error(t("coupon.form.positive_int")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ min: "1" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={24} span={24}>
              <Form.Item
                name={"targetType"}
                label={t("coupon.form.apply_to")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                initialValue="COUNTRY"
              >
                <Radio.Group
                  onChange={(e) => {
                    form.setFieldsValue({ salonIds: [] });
                    setSalonOptions([]);
                    if (e.target.value === "OUTLET") {
                      loadSalons(form.getFieldValue("countryIds") || []);
                    }
                    handleFormValuesChange();
                  }}
                >
                  <Radio value="COUNTRY">{t("coupon.form.target_country")}</Radio>
                  <Radio value="OUTLET">{t("coupon.form.target_outlet")}</Radio>
                </Radio.Group>
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"countryIds"}
                label={targetType === "OUTLET" ? t("coupon.form.country_to_list") : t("coupon.form.countries")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: t("coupon.form.countries_required"),
                  },
                ]}
              >
                <SelectField
                  multiple
                  options={countryOptions}
                  placeholder={t("coupon.form.countries_ph")}
                  onChange={(values) => {
                    const ids = values as number[];
                    if (form.getFieldValue("targetType") === "OUTLET") {
                      const keep = salonOptions.filter((o) =>
                        (form.getFieldValue("salonIds") || []).includes(o.value)
                      );
                      loadSalons(ids, keep);
                    }
                    handleFormValuesChange();
                  }}
                />
              </Form.Item>
          </Col>
          {targetType === "OUTLET" && (
            <Col md={12} span={24}>
              <Form.Item
                name={"salonIds"}
                label={t("coupon.form.salons")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: t("coupon.form.salons_required"),
                  },
                ]}
              >
                <SelectField
                  multiple
                  options={salonOptions}
                  placeholder={t("coupon.form.salons_ph")}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"appUserIds"}
                label={t("coupon.form.customers")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.length > 0) {
                        if (!Array.isArray(value) || value.some(id => !id)) {
                          return Promise.reject(new Error(t("coupon.form.customers_invalid")));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <SelectField
                  multiple
                  options={userList}
                  placeholder={t("coupon.form.customers_ph")}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={24} span={24}>
              <Form.Item
                name={"description"}
                label={t("coupon.form.description")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField multiline minRows={5} />
              </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
              <Form.Item
                name="applyToFirstOrderOnly"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ marginBottom: 0, textAlign: 'left', display: 'flex', alignItems: 'center' }}
                valuePropName="checked"
              >
                <Checkbox id="checkBox" style={{ marginRight: 8 }}>
                  {t("coupon.form.first_order_only")}
                </Checkbox>
              </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{t("coupon.form.active")}</span>
                <Form.Item
                  name={"active"}
                  style={{ margin: 0 }}
                  valuePropName="checked"
                >
                  <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
                </Form.Item>
            </div>
          </Col>
        </Row>
      </StyledForm>
    </Modal>
  );
};

export default AddModal;
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 10px;
  }
`;
